import React, { useState } from 'react';
import { SimpleGrid, useDisclosure, Text, Box, useToast, VStack, Icon } from '@chakra-ui/react';
import { FiInbox } from 'react-icons/fi';
import CampaignCard from './CampaignCard';
import ConfirmDialog from '../common/ConfirmDialog';
import LoadingSpinner from '../layout/LoadingSpinner';
import { useDeleteCampaign, useRenameCampaign } from '../../hooks/useCampaigns';

const CampaignList = ({ campaigns, isLoading, error }) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteCampaignMutation = useDeleteCampaign();
  const renameCampaignMutation = useRenameCampaign();
  const toast = useToast();

  // Handle the delete action
  const handleDeleteClick = (id) => {
    setSelectedCampaignId(id);
    onOpen();
  };

  const handleRename = async (id, name) => {
    try {
      return await renameCampaignMutation.mutateAsync({ id, name });
    } catch (err) {
      console.error('Error renaming campaign:', err);
      throw err;
    }
  };

  // Confirm and delete the campaign
  const confirmDelete = async () => {
    try {
      await deleteCampaignMutation.mutateAsync(selectedCampaignId);
      onClose();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      onClose();
      const isLocked = err?.response?.status === 423 || err?.message?.includes('423');
      toast({
        title: isLocked ? 'Campaign is locked' : 'Failed to delete campaign',
        description: isLocked
          ? 'This campaign has been exported or launched and cannot be deleted.'
          : err?.response?.data?.detail || err?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading campaigns..." />;
  }

  if (error) {
    return (
      <Box
        p={4}
        bg="#2d333b"
        borderRadius="md"
        border="1px solid rgba(252, 165, 165, 0.35)"
        color="#fca5a5"
      >
        <Text fontSize="sm" lineHeight={1.6}>
          Error loading campaigns: {error.message}
        </Text>
      </Box>
    );
  }

  if (!campaigns || campaigns.length === 0) {
    return (
      <Box py={{ base: 6, md: 8 }} px={{ base: 4, md: 6 }} textAlign="center">
        <VStack spacing={5}>
          <Icon as={FiInbox} boxSize={12} color="rgba(255,255,255,0.88)" strokeWidth={1} />
          <Text fontSize="lg" fontWeight="700" color="#fafafa" letterSpacing="-0.02em">
            No campaigns found
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <>
      <SimpleGrid columns={[1, null, 2, 3]} spacing={4} px={1}>
        {campaigns.map((campaign) => (
          <CampaignCard
            key={campaign.id || campaign._id}
            campaign={campaign}
            onDelete={handleDeleteClick}
            onRename={handleRename}
          />
        ))}
      </SimpleGrid>

      <ConfirmDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign? This action cannot be undone."
        confirmText="Delete"
        isLoading={deleteCampaignMutation.isLoading}
      />
    </>
  );
};

export default CampaignList; 